import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../../store";
import {first, switchMap, takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {IBoosterBenchmark} from "../../../../../store/reducers/booster.reducer";
import {BoosterService} from "../../services/booster.service";
import {BoosterBenchmark} from "./models/booster-benchmark.model";
import benchmarkErrors from "./benchmark-errors.json";
import {BenchmarkMetrics} from "./benchmark-metrics";
import {MigrationService} from "../../services/migration.service";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";
import {UserSubscription} from "../../../../../user/user-subscription";
import {DbModalService} from "../../../../../modules/modal/modal";
import {User} from "../../../../../user/user";
import {PaymentDialogComponent} from "../../../../../workspace/billing/payment-dialog/payment-dialog.component";
import {DialogService} from "../../../../../../ng2-bootstrap-modal/dialog.service";
import {ActivatedRoute, Router} from "@angular/router";
import {Utils} from "../../../../../shared/utils/utils";
import {TypeUtils} from "../../../../../shared/utils/type-utils";
import {
  OneWeekFreeDemoUpgradeComponent
} from "../../../../../shared/components/dialogs/one-month-free/components/one-week-free-demo-upgrade/one-week-free-demo-upgrade.component";

@Component({
    selector: 'db-benchmark',
    templateUrl: './benchmark.component.html',
    styleUrls: ['./benchmark.component.scss'],
    standalone: false
})
export class BenchmarkComponent implements OnInit, OnDestroy {

  @Input() companyName: string;
  @Input() websiteId: number;
  @Input() isHosted: boolean;
  @Input() userSubscription: UserSubscription;
  @Input() user: User;
  @Input() hostedCounts: any;

  public benchmark: IBoosterBenchmark = {
    phpVersion: null,
    mysqlVersion: null,
    serverPerformance: null,
    wordpressPerformance: null
  };
  public loading: boolean = true;
  public demoUser: boolean;

  public readonly benchmarkMetrics = BenchmarkMetrics;
  public readonly benchmarkErrors: { [key: string]: string } = benchmarkErrors;

  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _store: Store<fromStore.DbStoreState>,
    private _dialogService: DialogService,
    private _boosterService: BoosterService,
    private _migrationService: MigrationService,
    private _dbModalService: DbModalService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _modalService: DbModalService
  ) {
  }

  ngOnInit() {
    this._addEventListeners();
    this.demoUser = Utils.isDemoUser(this.userSubscription, this.user);
    if (this.demoUser) this.loading = false;
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private _addEventListeners(): void {
    this._store.select(fromStore.getWebsiteBoosterStateLoaded(this.websiteId))
      .pipe(
        first(loaded => loaded),
        switchMap(() => this._store.select(fromStore.getWebsiteBoosterBenchmark(this.websiteId))),
        takeUntil(this._destroyStream$)
      )
      .subscribe(benchmark => {
        this.loading = false;
        this.benchmark = benchmark ? benchmark : this.benchmark;
        if ((!this.benchmark || this.benchmark.serverPerformance === null) && !this.demoUser) this.rerunBenchmark();
      });
  }

  public rerunBenchmark(): void {
    if (!!this.demoUser) this.openCheckoutDialog();
    this.loading = true;
    this._boosterService.rerunBenchmark(this.websiteId)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(response => {
        if (response.success) {
          this.benchmark = new BoosterBenchmark(response.data);
          this._store.dispatch(new fromStore.LoadBoosterBenchmarkSuccess({
            websiteId: this.websiteId,
            benchmark: this.benchmark
          }));
        }
        this.loading = false;
      });
  }

  public openCheckoutDialog(): void {
    const analyticsData = {
      eventCategory: 'Free Upgrade Offer',
      eventAction: 'Locked button click',
      eventLabel: 'Speed Booster: Benchmark reanalyze'
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    const firstTime = TypeUtils.isNull(localStorage.getItem('dbShowFirstTime'));
    const options = {
      overflowHidden: false,
    };
    if (!firstTime) {
      options['width'] = 1240;
    } else {
      options['width'] = 460;
      options['height'] = 600;
    }
    const modal = this._modalService.open(OneWeekFreeDemoUpgradeComponent, options);
    modal.componentInstance.showFirstTime = firstTime;
  }

  public buyNow(): void {
    if (this.userSubscription && this.userSubscription.trialBoosterClient) {
      const analyticsData = {
        eventCategory: 'Dashboard Action',
        eventAction: 'Upgrade button click',
        eventLabel: 'Speed Booster: Hosting page: Comparison table',
        websiteId: this.websiteId
      };
      this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
      this._router.navigate([`/upgrade-plan`]);
    } else {
      this._dialogService.addDialog(PaymentDialogComponent, {
        upgradeSubscription: this.userSubscription,
        fromDialog: true,
        renew: false,
        showAdditional: true,
        fromHosting: true,
        extend: false,
        buyCdnAddOn: false,
        websiteId: this.websiteId
      });
    }
  }

  public migrate(): void {
    this._migrationService.migrate(this.websiteId);
  }
}
